import React, { useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { Permission, getPermissionByKey, getPermissionKeys } from './permissions';

const allUserPermissions = new Map();

export const can = (permissions, or = false) => {
  if (typeof permissions === 'function') {
    return permissions(can);
  }

  if (!allUserPermissions) {
    return false;
  }

  if (permissions instanceof Permission) {
    return allUserPermissions.has(permissions.name);
  }

  if (Array.isArray(permissions)) {
    const has = (permission) => allUserPermissions.has(permission.name);
    return or ? permissions.some(has) : permissions.every(has);
  }

  return false;
};

const Authorize = ({
  permissions,
  or,
  children,
  fallback,
}) => {
  if (can(permissions, or)) {
    return children;
  }

  return fallback || null;
};

const loadAdminPermissions = () => {
  getPermissionKeys().forEach((key) => {
    const permission = getPermissionByKey(key);
    if (permission) {
      allUserPermissions.set(permission.name, permission);
    }
  });
};

const loadRolePermissions = (role) => {
  role.permissions.forEach((permissionName) => {
    const permission = getPermissionByKey(permissionName);
    if (permission) {
      allUserPermissions.set(permission.name, permission);
    } else {
      console.warn(`Unknown permission ${permissionName}`);
    }
  });
};

const loadUserRolePermissions = (userRoles, roles) => {
  userRoles.forEach((userRoleName) => {
    const role = roles.find(({ name }) => name === userRoleName);
    if (role) {
      loadRolePermissions(role);
    } else {
      console.warn('User role does not exist');
    }
  });
};

const initializePermissions = (userRoles, roles) => {
  if (!userRoles) {
    return;
  }

  allUserPermissions.clear();

  if (userRoles.includes('admin')) {
    loadAdminPermissions();
  } else {
    loadUserRolePermissions(userRoles, roles);
  }
};

const withPermissions = (WrappedAuthorize) => ({ userRoles, roles, ...props }) => {
  useMemo(() => {
    initializePermissions(userRoles, roles);
  }, [userRoles, roles]);

  return <WrappedAuthorize {...props} />;
};

export default compose(
  connect(({
    authentication: {
      roles: userRoles,
    },
    roles: {
      roles,
    },
  }) => ({ userRoles, roles })),
  withPermissions,
)(Authorize);
